import React, { useEffect, useState } from 'react';

import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import SellerInfo from './components/SellerInfo/SellerInfo';
import SellerContactOptions from './components/SellerContactOptions/SellerContactOptions';
import { TListingClient } from 'types/TListing';
import { getHeroImageUrl } from '../../utils/getImageUrls';
import { TSellerProfileClient } from 'types/TSellerProfile';
import getSellerProfile from '../../repositories/getSellerProfile';
import { trackGAEvent } from '../../utils/gaTracking';
import { GA_TRACK_EVENTS_ACTIONS, GA_PARAMETER } from '../../utils/constants';
import createCustomDimension from '../../utils/createCustomDimension';
import { calculateActiveYears } from '../../utils/calculateActiveSinceDiff';
import ListingHeader from './components/ListingHeader/ListingHeader';
import BiddingApp from '../BiddingApp/BiddingApp';
import { BANNER_POSITION, bannerPositions } from '../BannersApp/constants/banners';
import Banners from '../BannersApp/components/Banners/Banners';

type TSellerSidebarAppProps = {
  appState: {
    listing: TListingClient;
  };
  environment: any;
  showBannersOnVip: boolean;
};

const SellerSidebarApp = ({ appState, environment, showBannersOnVip }: TSellerSidebarAppProps) => {
  const [profile, setProfile] = useState<TSellerProfileClient>({});
  const {
    listing: {
      itemId,
      title,
      gallery,
      priceInfo,
      seller,
      isCarAd,
      isAutomotiveAd,
      traits,
      flags: { isAdmarkt, hasCallTracking, allowTradeInRequest },
      lowBidDetection,
      bidsInfo: { isBiddingEnabled },
    },
  } = appState;
  const showExtendedAsq = traits.includes('AUTOMOTIVE_EXTENDED_ASQ');

  useEffect(() => {
    (async () => {
      setProfile(
        await getSellerProfile({
          sellerId: seller.id,
          requestExternalReviews: seller.requestExternalReviews,
          showExtendedAsq,
        }),
      );
    })();
  }, [seller.id, seller.requestExternalReviews, showExtendedAsq]);

  const hasTrackedSellerInfo = React.useRef(false);

  useEffect(() => {
    if (profile && !hasTrackedSellerInfo.current) {
      const { bankAccount, identification, paymentMethod, averageScore, numberOfReviews } = profile;

      if (bankAccount || identification || paymentMethod || averageScore || numberOfReviews) {
        trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_INFO_AVAILABLE, '', {
          ...createCustomDimension({
            dimension: GA_PARAMETER.SELLER_INFO,
            payload: JSON.stringify({
              YearsActive: calculateActiveYears(seller.activeSince),
              ...(bankAccount && { BankAccount: bankAccount }),
              ...(identification && { IDChecked: identification }),
              ...(paymentMethod && { IdealPayment: paymentMethod?.name === 'ideal' }),
              StarRating: averageScore,
              NumberReviews: numberOfReviews,
            }),
          }),
        });

        hasTrackedSellerInfo.current = true;
      }
    }
  }, [profile, seller.activeSince]);

  return (
    <EnvironmentProvider value={environment}>
      <ListingHeader listing={appState.listing} />
      <SellerContactOptions
        itemId={itemId}
        title={title}
        adImageThumbnail={getHeroImageUrl(gallery.imageUrls[0])}
        priceInfo={priceInfo}
        seller={seller}
        isAutomotiveAd={isAutomotiveAd}
        allowTradeInRequest={allowTradeInRequest}
        isAdmarkt={isAdmarkt}
        hasCallTracking={hasCallTracking}
        traits={traits}
        lowBidDetection={lowBidDetection}
        profile={profile}
        isCarAd={isCarAd}
      />
      <SellerInfo seller={seller} isCarAd={isCarAd} profile={profile} />
      {isBiddingEnabled && (
        <div id="bidding-root" className="display-block-m">
          <BiddingApp appState={appState} />
        </div>
      )}
      {showBannersOnVip && (
        <div
          id={bannerPositions[BANNER_POSITION.STATIC.RIGHT].containerId}
          className="display-block-m right-banner-root-container"
        >
          <Banners position={BANNER_POSITION.STATIC.RIGHT} />
        </div>
      )}
    </EnvironmentProvider>
  );
};

export default SellerSidebarApp;
